<template>
  <a-form>
    <div v-if="genderOption" class="container">
      <div class="item-wrapper">
        <gender-picker :displayData="genderOption" class="item" />
      </div>
      <div class="item-wrapper">
        <country-picker class="item" />
      </div>
      <div class="item-wrapper">
        <birthday-picker class="item" />
      </div>
      <div class="item-wrapper">
        <post-code-picker class="item" />
      </div>
    </div>

    <div class="masonry">
      <div v-for="option in options" :key="getId(option.data)" class="item-wrapper">
        <component :is="option.control" :displayData="option.data" class="item" />
      </div>
    </div>

    <targeting-info-interests>
      <div class="masonry">
        <div v-for="option in interestsOptions" :key="getId(option.data)" class="item-wrapper">
          <component
            :is="option.control"
            :displayData="option.data"
            :controlProps="option.controlProps"
            class="item"
          />
        </div>
      </div>
    </targeting-info-interests>

    <div class="checkboxes">
      <allow-retargeting-checkbox class="checkbox-container" />
      <history-checkbox class="checkbox-container" />
      <digital-fingerprint-checkbox class="checkbox-container" />
      <allow-targeting-on-all-websites-checkbox class="checkbox-container" />
      <allow-personalised-ads-checkbox class="checkbox-container" />
    </div>

    <div class="button-container">
      <div class="item-wrapper">
        <fake-save-button class="fake-save-button" :message="$t('save.targeting.info')"></fake-save-button>
      </div>
      <div class="item-wrapper">
        <delete-targeting-info-button class="item button-container" />
      </div>
    </div>
  </a-form>
</template>

<script>
import { mapGetters } from 'vuex';
import createTargetingInfoSelect from '@/components/account/targetingInfo/createTargetingInfoSelect';
import { isSection, getSectionId } from '@/services/targetingInfo';

import { SECTION_CODES } from '@/constants';

import GenderPicker from '@/components/account/targetingInfo/GenderPicker.vue';
import BirthdayPicker from '@/components/account/targetingInfo/BirthdayPicker.vue';
import CountryPicker from '@/components/account/targetingInfo/CountryPicker.vue';
import PostCodePicker from '@/components/account/targetingInfo/PostCodePicker.vue';
import AllowRetargetingCheckbox from '@/components/account/targetingInfo/AllowRetargetingCheckbox.vue';
import AllowTargetingOnAllWebsitesCheckbox from '@/components/account/targetingInfo/AllowTargetingOnAllWebsitesCheckbox.vue';
import AllowPersonalisedAdsCheckbox from '@/components/account/targetingInfo/AllowPersonalisedAdsCheckbox.vue';
import HistoryCheckbox from '@/components/account/targetingInfo/HistoryCheckbox.vue';
import DigitalFingerprintCheckbox from '@/components/account/targetingInfo/DigitalFingerprintCheckbox.vue';
import DeleteTargetingInfoButton from '@/components/account/targetingInfo/DeleteTargetingInfoButton.vue';
import FakeSaveButton from '@/components/account/FakeSaveButton.vue';
import TargetingInfoInterests from '@/components/account/targetingInfo/TargetingInfoInterests.vue';

export default {
  components: {
    GenderPicker,
    BirthdayPicker,
    CountryPicker,
    HistoryCheckbox,
    DigitalFingerprintCheckbox,
    PostCodePicker,
    AllowRetargetingCheckbox,
    DeleteTargetingInfoButton,
    FakeSaveButton,
    TargetingInfoInterests,
    AllowTargetingOnAllWebsitesCheckbox,
    AllowPersonalisedAdsCheckbox,
  },
  computed: {
    ...mapGetters([
      'targetingInfoDemographicData',
      'targetingInfoInterestData',
    ]),
    options() {
      return this.targetingInfoDemographicData
        .filter(this.isAvailableSection)
        .map((option) => ({
          data: option,
          control: createTargetingInfoSelect(option),
        }));
    },
    interestsOptions() {
      return this.targetingInfoInterestData.map((option) => ({
        data: option,
        control: createTargetingInfoSelect(option, (section) => [
          getSectionId(section),
          (name) => ({ update: (i) => ({ [name]: i }) }),
        ]),
        controlProps: {
          multiple: true,
        },
      }));
    },
    genderOption() {
      return this.targetingInfoDemographicData.find(
        (i) => getSectionId(i) === SECTION_CODES.GENDER,
      );
    },
  },
  methods: {
    isAvailableSection(section) {
      const excludeSections = [
        SECTION_CODES.INTERESTS,
        SECTION_CODES.AGE_RANGE,
        SECTION_CODES.GENDER,
        SECTION_CODES.POST_CODE,
        SECTION_CODES.COUNTRY,
        SECTION_CODES.CITY,
      ];
      return (
        isSection(section) && !excludeSections.includes(getSectionId(section))
      );
    },
    getId(item) {
      return getSectionId(item);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/fonts';
@import '~@/assets/styles/colors';
@import '~@/assets/styles/variables';
@import '~@/assets/styles/breakpoints';

.item-wrapper {
  flex: 0 50%;
  margin: 16px 0;
}

.checkboxes {
  display: flex;
  flex-direction: column;
  font-size: 14px;
  letter-spacing: 0.13px;
  line-height: 18px;
}

.checkbox-container {
  padding: 20px 12px;
}

.container {
  display: flex;
  flex-wrap: wrap;
}

.item {
  max-width: 452px;
}

.button-container {
  display: flex;
  flex: 0 100%;
  justify-content: flex-end;
}

.masonry {
  display: flex;
  flex-flow: column wrap;
  max-height: 1100px;
}

@media (max-width: 1145px) {
  .masonry {
    max-height: 100%;
  }
  .container {
    flex-direction: column;
  }
}

@media (max-width: $login-base-breakpoint) {
  .button-container {
    flex-direction: column;
    align-items: center;

    * {
      margin: 0;
    }

    .item-wrapper {
      max-width: $page-mobile-small-breakpoint;
      width: 100%;
    }
  }
}
</style>
